import { useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { api, errMsg } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { BrandLogo } from "@/components/BrandLogo";
import { toast } from "sonner";
import { Lock, CheckCircle2, ShieldCheck } from "lucide-react";

/**
 * ResetPassword — reads ?token= from the URL and POSTs to /auth/reset-password
 * with the new password. Reuses existing backend flow.
 */
export default function ResetPassword() {
  const [sp] = useSearchParams();
  const token = sp.get("token") || "";
  const [pw, setPw] = useState("");
  const [pw2, setPw2] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const nav = useNavigate();

  const invalid = !token;
  const mismatch = pw && pw2 && pw !== pw2;
  const tooShort = pw && pw.length < 8;

  const submit = async (e) => {
    e.preventDefault();
    if (invalid || mismatch || tooShort) return;
    setLoading(true);
    try {
      await api.post("/auth/reset-password", { token, new_password: pw });
      setDone(true);
      toast.success("Password reset — please sign in with your new password.");
      setTimeout(() => nav("/login"), 2500);
    } catch (err) { toast.error(errMsg(err)); }
    setLoading(false);
  };

  return (
    <div className="min-h-screen grid place-items-center bg-secondary/40 px-4 py-10" data-testid="reset-password-page">
      <div className="w-full max-w-md">
        <div className="bg-card rounded-2xl border shadow-sm p-6">
          <div className="flex justify-center mb-4"><BrandLogo size={28} /></div>

          {done ? (
            <div className="text-center space-y-3" data-testid="rp-done">
              <div className="w-12 h-12 mx-auto rounded-full bg-emerald-100 text-emerald-700 grid place-items-center">
                <CheckCircle2 className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-display font-semibold">Password updated</h2>
              <p className="text-sm text-muted-foreground">Redirecting you to sign in…</p>
            </div>
          ) : invalid ? (
            <div className="text-center space-y-3" data-testid="rp-invalid">
              <div className="w-12 h-12 mx-auto rounded-full bg-destructive/10 text-destructive grid place-items-center">
                <ShieldCheck className="w-6 h-6" />
              </div>
              <h2 className="text-xl font-display font-semibold">Missing or invalid link</h2>
              <p className="text-sm text-muted-foreground">Please request a new reset link.</p>
              <Button asChild className="w-full mt-2">
                <Link to="/forgot-password">Request new link</Link>
              </Button>
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="rp-form">
              <div className="text-center">
                <h1 className="text-xl md:text-2xl font-display font-semibold">Reset your password</h1>
                <p className="text-sm text-muted-foreground mt-1">Choose a new password for your Babu Baazar account.</p>
              </div>
              <div>
                <Label>New password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="password" required autoFocus minLength={8} value={pw} onChange={(e) => setPw(e.target.value)} className="pl-9 h-10" data-testid="rp-pw" />
                </div>
                {tooShort && <p className="text-xs text-destructive mt-1">Minimum 8 characters</p>}
              </div>
              <div>
                <Label>Confirm password</Label>
                <div className="relative">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
                  <Input type="password" required minLength={8} value={pw2} onChange={(e) => setPw2(e.target.value)} className="pl-9 h-10" data-testid="rp-pw2" />
                </div>
                {mismatch && <p className="text-xs text-destructive mt-1">Passwords don&apos;t match</p>}
              </div>
              <Button type="submit" disabled={loading || !pw || !pw2 || mismatch || tooShort} className="w-full btn-primary-glow h-11" data-testid="rp-submit">
                {loading ? "Updating…" : "Update password"}
              </Button>
              <Link to="/login" className="block text-center text-sm text-muted-foreground hover:text-primary" data-testid="rp-back">Back to sign in</Link>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
